import React, { useState, useRef, useEffect } from 'react';
import Modal from 'react-modal';
import axios from 'axios';
import EntryItem from './entry-item';

// Set the app element for accessibility (matches your index.html)
Modal.setAppElement('#app');

// Custom modal styling
const modalStyles = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 999,
    display: 'flex',             // Use flexbox on the overlay
    justifyContent: 'center',    // Center horizontally
    alignItems: 'center',        // Center vertically
  },
  content: {
    position: 'relative',        // No absolute positioning
    inset: 'auto',               // Let it size naturally
    background: 'var(--app-div-background-color)',
    color: 'var(--app-primary-text-color)',
    border: '1px solid #444',
    borderRadius: '5px',
    padding: '1rem',
    width: '400px',
    maxWidth: '90%',             // Keep it responsive
    maxHeight: '80vh',           // Prevent overflow on small screens
    overflow: 'auto',            // Scroll inside if content is too tall
    margin: '0 auto',            // Center horizontally if needed
  },
};

const EntriesList = ({
  entries,
  onRemoveEntries, 
  API_BASE_URL,
  PORT,
  activeServer,
  gameId
}) => {
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [lastClickedIndex, setLastClickedIndex] = useState(null);
  const [actionEntry, setActionEntry] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const containerRef = useRef(null);

  const handleEntryClick = (entry, index, e) => {
    if (e.shiftKey) {
      if (lastClickedIndex === null) {
        setSelectedEntries([entry.id]);
      } else {
        const start = Math.min(lastClickedIndex, index);
        const end = Math.max(lastClickedIndex, index);
        const rangeIds = entries.slice(start, end + 1).map((item) => item.id);
        setSelectedEntries(rangeIds);
      }
      setLastClickedIndex(index);
      return;
    }
    if (e.ctrlKey || e.metaKey) {
      setSelectedEntries((prev) =>
        prev.includes(entry.id)
          ? prev.filter((id) => id !== entry.id)
          : [...prev, entry.id]
      );
      setLastClickedIndex(index);
      return;
    }
    // Normal click = single selection
    setSelectedEntries([entry.id]);
    setLastClickedIndex(index);
  };

  // Remove icon click -> show confirmation
  const handleRemoveClick = (entry, e) => {
    e.stopPropagation();
    setActionEntry(entry);
    setShowConfirm(true);
  };

  // Pressing Enter if multiple selected -> remove them all
  const handleContainerKeyDown = (e) => {
    if (e.key === 'Enter' && selectedEntries.length > 1) {
      setActionEntry(null);
      setShowConfirm(true);
    }
  };

  // Called on "Yes" in the modal
  const handleRemoveConfirm = () => {
    if (actionEntry) {
      // Single entry removal
      axios
        .delete(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${gameId}/${actionEntry.id}`)
        .then((response) => {
          console.log('Deleted single entry:', response.data);
          onRemoveEntries([actionEntry]);
        })
        .catch((err) => console.error('Delete error:', err));
    } else {
      // Multi-removal
      const selected = entries.filter((e) => selectedEntries.includes(e.id));
      const promises = selected.map((entry) =>
        axios.delete(`${API_BASE_URL}:${PORT}/api/games/${activeServer.id}/game/${gameId}/${entry.id}`)
      );
      Promise.all(promises)
        .then(() => {
          console.log('Deleted multiple entries');
          onRemoveEntries(selected);
        })
        .catch((err) => console.error('Batch delete error:', err));
    }
    setShowConfirm(false);
    setActionEntry(null);
    setSelectedEntries([]);
    setLastClickedIndex(null);
  };

  // Called on "No" or clicking overlay
  const handleCloseModal = () => {
    setShowConfirm(false);
    setActionEntry(null);
  };

  // Unselect entries when clicking outside of this component
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setSelectedEntries([]);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      className="entries-list"
      onKeyDown={handleContainerKeyDown}
      tabIndex={0}
      ref={containerRef}
    >
      {entries.map((entry, index) => (
        <EntryItem
          key={entry.id}
          entry={entry}
          isSelected={selectedEntries.includes(entry.id)}
          onClick={(e) => handleEntryClick(entry, index, e)}
          onRemoveClick={(e) => handleRemoveClick(entry, e)}
        />
      ))}

      <Modal
        isOpen={showConfirm}
        onRequestClose={handleCloseModal}
        style={modalStyles}
        shouldCloseOnOverlayClick={true}
      >
        <div>
          <p>
            Are you sure you want to remove{' '}
            {actionEntry
              ? actionEntry.chatter.username
              : `${selectedEntries.length} entries`}
            {' '}from the game?
          </p>
          <span className="modal-buttons">
            <button className="modal-button" onClick={handleRemoveConfirm}>Yes</button>
            <button className="modal-button" onClick={handleCloseModal}>No</button>
          </span>
        </div>
      </Modal>
    </div>
  );
};

export default EntriesList;
